import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../lib/i18n';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import OnboardingProgress from './OnboardingProgress';
import WelcomeStep from './steps/WelcomeStep';
import ImportStep from './steps/ImportStep';
import SampleSwipeStep from './steps/SampleSwipeStep';
import MusicStep from './steps/MusicStep';
import VoiceoverStep from './steps/VoiceoverStep';
import SocialsStep from './steps/SocialsStep';
import ActivateStep from './steps/ActivateStep';
import { Home, ChevronLeft, ChevronRight, Sparkles, Globe } from 'lucide-react';
import { authAPI, onboardingAPI, propertiesAPI } from '../../lib/api';
import { markOnboardingComplete } from '../../lib/onboarding';
import { track } from '../../lib/analytics';
import { toast } from 'sonner';

// 1 Profile · 2 Property · 3 Sample swipe · 4 Music · 5 Voiceover · 6 Socials · 7 Activate
const TOTAL_STEPS = 7;

// Legacy key. We used to keep an in-flight OnboardingSampleBatch id in
// localStorage so a tab reload resumed the same deck. The deck now reads
// straight off the catalog (`/api/templates`) so there's nothing to
// resume; we just best-effort clean up the stale key on mount.
const LEGACY_SAMPLE_BATCH_LS_KEY = 'onboarding_sample_batch';

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { t, lang, setLanguage } = useTranslation();
  const [step, setStep] = useState(1);
  const [userTier, setUserTier] = useState('free');

  // Shared state across steps
  const [wizardData, setWizardData] = useState({
    // Step 1: Profile
    profile: {
      name: '',
      brand_name: '',
      phone_number: '',
      profile_image: '',
      company_logo: '',
    },
    // Step 2: Properties
    propertiesImported: 0,
    justimmoConnected: false,
    // Step 3: Sample swipe. Catalog-driven now; votes land directly in
    // these two arrays and get committed into AutoPostConfig at the
    // Activate step. No batch id, no polling, no per-user Creatomate
    // render.
    likedTemplates: [],
    dislikedTemplates: [],
    // Step 4: Music. Multi-select. Scheduler picks one per auto-post at
    // random, so liking 3-4 tracks gives the videos sonic variety
    // automatically.
    selectedMusicPresetIds: [],
    selectedMusicUploadUrl: null,
    // Step 5: Voiceover. `voiceId` is one of:
    //   * a curated VoicePreset key (e.g. "matilda". The default)
    //   * a raw ElevenLabs voice_id from the advanced field
    //   * null = use the template's hardcoded default voice (back-compat
    //     for users who skipped the picker)
    enableVoiceover: true,
    voiceId: null,
    // Step 6: Socials
    connectedSocials: [],
    // Step 7: Activate
    autoPostConfig: {
      posts_per_day: 1,
      preferred_time: '10:00',
      timezone: 'Europe/Vienna',
    },
  });

  const updateData = useCallback((key, value) => {
    setWizardData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Load subscription tier so the sample-swipe step can show the right
  // "free vs paid" banner and the start-samples endpoint picks the right
  // sampler size. Non-fatal if it fails. We assume free tier.
  useEffect(() => {
    (async () => {
      try {
        const me = await authAPI.getMe();
        setUserTier(me.data?.subscription_tier || 'free');
      } catch (e) {
        setUserTier('free');
      }
    })();
  }, []);

  // Pre-populate the "properties imported" count from the user's actual
  // DB record. Otherwise returning users (Justimmo already connected, all
  // properties already imported previously) see "0 Immobilien" on the
  // summary card even though they have dozens. Confusing on the victory
  // lap. ImportStep still calls `onImport(count)` when new ones are
  // pulled, so that path keeps working too.
  useEffect(() => {
    // AbortController so the in-flight fetch is actually cancelled
    // when the user closes the wizard mid-load. Previously a
    // `cancelled` flag stopped setState but the request itself kept
    // running, wasting bandwidth on a screen the user already left.
    // Axios honours the signal natively (v1+).
    const controller = new AbortController();
    (async () => {
      try {
        const res = await propertiesAPI.getAll({ signal: controller.signal });
        const n = Array.isArray(res.data) ? res.data.length : 0;
        setWizardData((prev) =>
          prev.propertiesImported >= n ? prev : { ...prev, propertiesImported: n }
        );
      } catch (e) {
        // CanceledError on unmount is expected. Anything else is
        // non-fatal. Keep the default 0 on failure.
        if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
          // (intentionally silent. Not worth toasting on the wizard)
        }
      }
    })();
    return () => controller.abort();
  }, []);

  // One-time cleanup of the old batch-id localStorage key so returning
  // users who started onboarding under the old flow don't leave that key
  // hanging around forever.
  useEffect(() => {
    localStorage.removeItem(LEGACY_SAMPLE_BATCH_LS_KEY);
  }, []);

  const handleNext = () => {
    if (step < TOTAL_STEPS) {
      // A13. Fires when the user clicks Next, so we measure step
      // completion (not just step view). `step` is the step they
      // just finished.
      track('wizard_step_completed', { step });
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const buildCompletionPayload = () => ({
    liked_templates: wizardData.likedTemplates,
    disliked_templates: wizardData.dislikedTemplates,
    music_preset_ids: wizardData.selectedMusicPresetIds || [],
    music_upload_url: wizardData.selectedMusicUploadUrl || '',
    enable_voiceover: wizardData.enableVoiceover,
    voice_id: wizardData.voiceId,
  });

  const finalize = async () => {
    try {
      await onboardingAPI.complete(buildCompletionPayload());
    } catch (e) {
      if (e.response?.status !== 401) {
        toast.error('Could not save onboarding status. Your settings may need to be re-entered.');
      }
    }
    // A13. Onboarding-complete is the most important funnel event
    // since it gates first render. Fire on both happy + skip path.
    track('onboarding_completed');
    markOnboardingComplete();
    navigate('/dashboard');
  };

  const handleComplete = finalize;

  const handleSkipToEnd = async () => {
    // Skipping preserves whatever state the wizard has collected so far –
    // if the user filled profile + liked one template then bailed, we
    // still save those.
    await finalize();
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <WelcomeStep
            data={wizardData.profile}
            onChange={(profile) => updateData('profile', profile)}
          />
        );
      case 2:
        return (
          <ImportStep
            onImport={(count) => updateData('propertiesImported', count)}
            onConnect={(connected) => updateData('justimmoConnected', connected)}
            importedCount={wizardData.propertiesImported}
            isConnected={wizardData.justimmoConnected}
          />
        );
      case 3:
        return (
          <SampleSwipeStep
            isFreeTier={userTier === 'free'}
            likedTemplates={wizardData.likedTemplates}
            dislikedTemplates={wizardData.dislikedTemplates}
            onLikedChange={(v) => updateData('likedTemplates', v)}
            onDislikedChange={(v) => updateData('dislikedTemplates', v)}
            onSkip={handleNext}
          />
        );
      case 4:
        return (
          <MusicStep
            selectedPresetIds={wizardData.selectedMusicPresetIds}
            selectedUploadUrl={wizardData.selectedMusicUploadUrl}
            onPresetIdsChange={(ids) => updateData('selectedMusicPresetIds', ids)}
            onUploadSelect={(url) => updateData('selectedMusicUploadUrl', url || null)}
            onClear={() => {
              updateData('selectedMusicPresetIds', []);
              updateData('selectedMusicUploadUrl', null);
            }}
          />
        );
      case 5:
        return (
          <VoiceoverStep
            enableVoiceover={wizardData.enableVoiceover}
            voiceId={wizardData.voiceId}
            onChange={({ enableVoiceover, voiceId }) => {
              // Single setState so back/forward into the step doesn't see
              // a half-applied state where the yes/no was committed but
              // the voice wasn't, or vice versa.
              setWizardData((prev) => ({
                ...prev,
                enableVoiceover:
                  enableVoiceover !== undefined ? enableVoiceover : prev.enableVoiceover,
                voiceId: voiceId !== undefined ? voiceId : prev.voiceId,
              }));
            }}
          />
        );
      case 6:
        return (
          <SocialsStep
            connectedSocials={wizardData.connectedSocials}
            onSocialsChange={(socials) => updateData('connectedSocials', socials)}
          />
        );
      case 7:
        return (
          <ActivateStep
            config={wizardData.autoPostConfig}
            onConfigChange={(config) => updateData('autoPostConfig', config)}
            wizardData={wizardData}
            onComplete={handleComplete}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 " />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#dfe8db] rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#f5eede] rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative border-b border-[#e3dccb] backdrop-blur-xl bg-[#f7f3ea]/85">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#1f4d3a] rounded-lg glow-primary">
              <Home className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#1f4d3a] to-[#1f4d3a] bg-clip-text text-transparent">
              Real Content
            </h1>
          </div>
          <div className="flex items-center gap-1">
            {/* Language toggle. Onboarding renders before the user hits
                AppLayout, so without this the wizard is locked to whatever
                'reeltypost_lang' was set to (default 'de'). Surface a tiny
                EN/DE flip here so English-speakers see the toggle right
                on the welcome screen instead of having to finish all 7
                steps in German just to find the global header. */}
            <Button
              variant="ghost"
              onClick={() => setLanguage(lang === 'de' ? 'en' : 'de')}
              className="text-[#6f6a5c] hover:text-[#23211a] text-xs px-2"
              title="DE / EN"
            >
              <Globe className="w-4 h-4 mr-1" />
              <span className="uppercase font-medium">{lang}</span>
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkipToEnd}
              className="text-[#8a8474] hover:text-[#4a463b] text-xs"
            >
              {t('common.skip')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <OnboardingProgress currentStep={step} totalSteps={TOTAL_STEPS} />
        </div>

        <Card className="glass border-[#e3dccb]">
          <CardContent className="p-6 md:p-8">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === 1}
            className="border-[#d8d0bd] text-[#4a463b]"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('common.back')}
          </Button>

          {step < TOTAL_STEPS ? (
            <Button
              onClick={handleNext}
              className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
            >
              {t('common.next')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              className="bg-[#1e7a4f] hover:bg-[#155c3b] text-white"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              {t('onboarding.step7.congratulations')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
